import { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

interface Props {
  /** Raw markdown source as stored in the DB. */
  source: string;
}

/**
 * Authors write posts in a casual, plain-text style (no `**bold**`, no
 * `# headers`). To give existing posts visual structure without forcing a
 * manual edit pass on the DB, we auto-promote a handful of conventional
 * patterns to real markdown right before rendering:
 *
 *   • Typographic bullets ({@code •}, {@code ·}, {@code ●}) become proper
 *     markdown list items so the renderer emits {@code <ul>/<li>} with a
 *     styled marker, not bare paragraphs.
 *   • Uppercase labels followed by an em-dash or colon
 *     ({@code ÉTAPE 1 — …}, {@code EXEMPLE CHIFFRÉ : …},
 *     {@code RÉSULTATS ATTENDUS :}) get bolded. The pattern requires the
 *     label to be at the START of a line and ALL-CAPS — that's strict
 *     enough that mid-sentence colons ({@code L'ennemi #1 du débutant :
 *     …}) and proper names ({@code Trade Republic (…)}) are never matched.
 *
 * The transformation is idempotent: a label that's already wrapped in
 * `**` won't match again because `*` isn't in the allowed character class.
 */
function enhanceMarkdown(source: string): string {
  let text = source;

  // 1. Typographic bullet → markdown list item.
  text = text.replace(/^[•·●]\s+/gm, '- ');

  // 2. Inline form: "ÉTAPE 1 — Ouvre un compte…" → "**ÉTAPE 1 —** Ouvre…"
  text = text.replace(
    /^([A-ZÀ-Ÿ][A-ZÀ-Ÿ0-9\s'#\-]{2,49}?\s*[—–:])(\s+)/gm,
    '**$1**$2',
  );

  // 3. Standalone form: "RÉSULTATS ATTENDUS :" on its own line.
  text = text.replace(
    /^([A-ZÀ-Ÿ][A-ZÀ-Ÿ0-9\s'#\-]{2,49}?\s*[—–:])\s*$/gm,
    '**$1**',
  );

  return text;
}

/**
 * Renders a post body as markdown. {@code react-markdown} discards raw HTML
 * by default, so callers don't need to sanitize before passing the string —
 * a hostile author can't slip in {@code <script>} or {@code onerror=}.
 *
 * <p>{@code remarkGfm} adds tables, task lists, strikethrough and
 * autolinks. {@code remarkBreaks} converts soft line breaks ({@code \n})
 * into hard breaks so the formatting looks like what the author typed.</p>
 *
 * <p>External links open in a new tab with {@code rel="noopener noreferrer"}
 * — never trust a URL pasted in a post not to be a phishing target.</p>
 */
export const PostBody = memo(function PostBody({ source }: Props) {
  const enhanced = useMemo(() => enhanceMarkdown(source), [source]);

  return (
    <Box
      sx={{
        // Inline styles target the markdown elements emitted by react-markdown.
        // Tight enough to feel native to MUI, loose enough to keep prose
        // readable on long-form posts.
        color: 'text.primary',
        lineHeight: 1.75,
        fontSize: { xs: 15.5, md: 16.5 },
        wordBreak: 'break-word',
        '& p': { margin: '0 0 1.05em' },
        '& p:last-child': { marginBottom: 0 },
        '& strong': {
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: 0.1,
        },
        '& h1, & h2, & h3, & h4': {
          fontWeight: 700,
          lineHeight: 1.25,
          marginTop: '1.4em',
          marginBottom: '0.4em',
        },
        '& h1': { fontSize: '1.6em' },
        '& h2': { fontSize: '1.35em' },
        '& h3': { fontSize: '1.15em' },
        '& h4': { fontSize: '1em' },
        '& ul, & ol': { paddingLeft: '1.5em', margin: '0.2em 0 1.05em' },
        '& li': { margin: '0.35em 0' },
        '& li::marker': { color: 'primary.main', fontWeight: 700 },
        '& li > p': { margin: 0 },
        '& blockquote': {
          margin: '0 0 0.9em',
          paddingLeft: '0.9em',
          borderLeft: '3px solid',
          borderColor: 'divider',
          color: 'text.secondary',
        },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.92em',
          padding: '0.12em 0.35em',
          borderRadius: 4,
          bgcolor: 'action.hover',
        },
        '& pre': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.9em',
          padding: '0.9em 1em',
          borderRadius: 1.5,
          bgcolor: 'action.hover',
          overflow: 'auto',
          margin: '0 0 0.9em',
        },
        '& pre > code': { padding: 0, bgcolor: 'transparent' },
        '& a': {
          color: 'primary.main',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
        },
        '& a:hover': { textDecoration: 'none' },
        '& hr': {
          border: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          margin: '1.5em 0',
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          margin: '0 0 0.9em',
          fontSize: '0.95em',
        },
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          padding: '0.4em 0.65em',
        },
        '& th': { bgcolor: 'action.hover', fontWeight: 600 },
        '& img': { maxWidth: '100%', borderRadius: 8 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        // react-markdown@9+ ignores raw HTML by default. Setting the link
        // renderer to open in a new tab is the only customisation we need.
        components={{
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {enhanced}
      </ReactMarkdown>
    </Box>
  );
});
